import * as C from '../constants';
import piwikTracker from '../utils/piwik';

export const addDataItemToCart = (items: DataItem[]): AddToCartAction =>
  trackAddCartDataItems(items) && addToCart(items, C.CART_KEY.DATA_ITEM);

export const addShopItemToCart = (items: ShopItem[]): AddToCartAction =>
  trackAddCartShopItems(items) && addToCart(items, C.CART_KEY.SHOP_ITEM);

export const addShopItemCategoryToCart = (
  items: ShopItemCategory[]
): AddToCartAction =>
  trackAddCartShopItemCategories(items) &&
  addToCart(items, C.CART_KEY.SHOP_ITEM_CATEGORY);

export const removeDataItemFromCart = (
  items: DataItem[]
): RemoveFromCartAction =>
  trackRemoveCartDataItems(items) &&
  removeFromCart(items, C.CART_KEY.DATA_ITEM);

export const removeShopItemFromCart = (
  items: ShopItem[]
): RemoveFromCartAction =>
  trackRemoveCartShopItems(items) &&
  removeFromCart(items, C.CART_KEY.SHOP_ITEM);

export const removeShopItemCategoryFromCart = (
  items: ShopItemCategory[]
): RemoveFromCartAction =>
  trackRemoveCartShopItemCategories(items) &&
  removeFromCart(items, C.CART_KEY.SHOP_ITEM_CATEGORY);

export const addToCart = (
  items: CartItem[],
  key: string = 'default'
): AddToCartAction => ({
  type: C.ACTION_TYPES.ADD_TO_CART,
  payload: {
    items,
    key,
  },
});

export const removeFromCart = (
  items: CartItem[],
  key: string = 'default'
): RemoveFromCartAction => ({
  type: C.ACTION_TYPES.REMOVE_FROM_CART,
  payload: {
    items,
    key,
  },
});

function trackAddCartDataItems(items: DataItem[]): true {
  const s = items.map(x => x.preference_product.name).join(', ');
  return trackCartEvent(C.ACTION_TYPES.ADD_TO_CART, C.CART_KEY.DATA_ITEM, s);
}

function trackRemoveCartDataItems(items: DataItem[]): true {
  const s = items.map(x => x.preference_product.name).join(', ');
  return trackCartEvent(
    C.ACTION_TYPES.REMOVE_FROM_CART,
    C.CART_KEY.DATA_ITEM,
    s
  );
}

function trackAddCartShopItems(items: ShopItem[]): true {
  const s = items.map(x => x.name).join(', ');
  return trackCartEvent(C.ACTION_TYPES.ADD_TO_CART, C.CART_KEY.SHOP_ITEM, s);
}

function trackRemoveCartShopItems(items: ShopItem[]): true {
  const s = items.map(x => x.name).join(', ');
  return trackCartEvent(
    C.ACTION_TYPES.REMOVE_FROM_CART,
    C.CART_KEY.SHOP_ITEM,
    s
  );
}

function trackAddCartShopItemCategories(items: ShopItemCategory[]): true {
  const s = items.map(x => x.name).join(', ');
  return trackCartEvent(
    C.ACTION_TYPES.ADD_TO_CART,
    C.CART_KEY.SHOP_ITEM_CATEGORY,
    s
  );
}

function trackRemoveCartShopItemCategories(items: ShopItemCategory[]): true {
  const s = items.map(x => x.name).join(', ');
  return trackCartEvent(
    C.ACTION_TYPES.REMOVE_FROM_CART,
    C.CART_KEY.SHOP_ITEM_CATEGORY,
    s
  );
}

function trackCartEvent(actionType: string, type: string, value: string): true {
  piwikTracker.trackEvent(
    C.ANALYTICS.CATEGORY_CATALOG,
    actionType,
    `[${type}] ${value}`
  );
  return true;
}

export function removeCartCacheItems() {
  window.localStorage.removeItem('dataItems');
  window.localStorage.removeItem('shopItems');
  window.localStorage.removeItem('shopItemCategories');
  window.localStorage.removeItem('needLoadItems');
}

export function getCartCacheItems() {
  const dataItems = window.localStorage.getItem('dataItems');
  const shopItems = window.localStorage.getItem('shopItems');
  const shopItemCategories = window.localStorage.getItem('shopItemCategories');
  const needLoadItems = window.localStorage.getItem('needLoadItems');
  return { dataItems, shopItems, shopItemCategories, needLoadItems };
}

function setNeedLoadItems() {
  window.localStorage.setItem('needLoadItems', 'false');
}

export function setNeedLoadItemsTrue() {
  window.localStorage.setItem('needLoadItems', 'true');
}

export function appendDataItemToCartCache(dataItem: DataItem) {
  setNeedLoadItems();
  const dataItems = window.localStorage.getItem('dataItems');
  if (dataItems) {
    let dataItemsJSON: DataItem[] = JSON.parse(dataItems);
    const newDataItemsJSON = dataItemsJSON.concat(dataItem);
    let dataItemsString = JSON.stringify(newDataItemsJSON);
    window.localStorage.setItem('dataItems', dataItemsString);
  } else {
    window.localStorage.setItem('dataItems', JSON.stringify([dataItem]));
  }
}

export function removeDataItemFromCartCache(dataItem: DataItem) {
  const dataItems = window.localStorage.getItem('dataItems');
  if (dataItems) {
    setNeedLoadItems();
    let dataItemsJSON: DataItem[] = JSON.parse(dataItems);
    const newDataItemsJSON = dataItemsJSON.filter(
      item => item.id != dataItem.id
    );
    let dataItemsString = JSON.stringify(newDataItemsJSON);
    window.localStorage.setItem('dataItems', dataItemsString);
  }
}

export function appendShopItemToCartCache(shopItem: ShopItem) {
  setNeedLoadItems();
  const shopItems = window.localStorage.getItem('shopItems');
  if (shopItems) {
    let shopItemsJSON: ShopItem[] = JSON.parse(shopItems);
    const newShopItemsJSON = shopItemsJSON.concat(shopItem);
    let shopItemsString = JSON.stringify(newShopItemsJSON);
    window.localStorage.setItem('shopItems', shopItemsString);
  } else {
    window.localStorage.setItem('shopItems', JSON.stringify([shopItem]));
  }
}

export function removeShopItemFromCartCache(shopItem: ShopItem) {
  const shopItems = window.localStorage.getItem('shopItems');
  if (shopItems) {
    setNeedLoadItems();
    let shopItemsJSON: ShopItem[] = JSON.parse(shopItems);
    const newShopItemsJSON = shopItemsJSON.filter(
      item => item.id != shopItem.id
    );
    let shopItemsString = JSON.stringify(newShopItemsJSON);
    window.localStorage.setItem('shopItems', shopItemsString);
  }
}

export function appendShopItemCategoryToCartCache(
  shopItemCategory: ShopItemCategory
) {
  setNeedLoadItems();
  const shopItemCategories = window.localStorage.getItem('shopItemCategories');
  if (shopItemCategories) {
    let shopItemCategoriesJSON: ShopItemCategory[] = JSON.parse(
      shopItemCategories
    );
    const newShopItemCategoriesJSON = shopItemCategoriesJSON.concat(
      shopItemCategory
    );
    let shopItemCategoriesString = JSON.stringify(newShopItemCategoriesJSON);
    window.localStorage.setItem('shopItemCategories', shopItemCategoriesString);
  } else {
    window.localStorage.setItem(
      'shopItemCategories',
      JSON.stringify([shopItemCategory])
    );
  }
}

export function removeShopItemCategoryFromCartCache(
  shopItemCategory: ShopItemCategory
) {
  const shopItemCategories = window.localStorage.getItem('shopItemCategories');
  if (shopItemCategories) {
    setNeedLoadItems();
    let shopItemCategoriesJSON: ShopItemCategory[] = JSON.parse(
      shopItemCategories
    );
    const newShopItemCategoriesJSON = shopItemCategoriesJSON.filter(
      item => item.id != shopItemCategory.id
    );
    let shopItemCategoriesString = JSON.stringify(newShopItemCategoriesJSON);
    window.localStorage.setItem('shopItemCategories', shopItemCategoriesString);
  }
}

export const updateIsFetchingCart = (isFetching: boolean) => ({
  type: C.ACTION_TYPES.UPDATE_CART_IS_FETCHING,
  payload: {
    isFetching,
  },
});



// WEBPACK FOOTER //
// ./app/actions/CartActionCreators.ts